import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import ContentContainer from 'components/content-container/ContentContainer';
import PageHeader from 'components/page-header/PageHeader';
import KvinnehistorieScrubber from 'components/kvinnehistorie-scrubber/KvinnehistorieScrubber';
import KvinnehistorieMilestone from 'components/kvinnehistorie-milestone/KvinnehistorieMilestone';

import { IKvinnehistorieTimelinePage } from 'source/NFR.types';

// How long the rail's auto-pin stays out of the way after the user scrolls it themselves.
const navScrollGraceMs = 4000;

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The CMS reloads the preview when switching pages, so the edited event is handed back to the timeline this way.
const returnTargetKey = 'kvinnehistorie-timeline-return-target';
const returnTargetMaxAgeMs = 10 * 60 * 1000;

const rememberReturnTarget = (anchorId?: string | null) => {
  try {
    if (anchorId) window.sessionStorage.setItem(returnTargetKey, JSON.stringify({ anchorId, savedAt: Date.now() }));
  } catch {
    // Storage can be blocked; the timeline then simply opens at the top.
  }
};

const takeReturnTarget = (): string | null => {
  try {
    const stored = window.sessionStorage.getItem(returnTargetKey);
    window.sessionStorage.removeItem(returnTargetKey);
    if (!stored) return null;
    const { anchorId, savedAt } = JSON.parse(stored) as { anchorId?: string; savedAt?: number };
    return anchorId && savedAt && Date.now() - savedAt < returnTargetMaxAgeMs ? anchorId : null;
  } catch {
    return null;
  }
};

const KvinnehistorieTimelinePage: React.FC<IKvinnehistorieTimelinePage> = ({
  header,
  navigation,
  scrubber,
  milestones,
  activeAnchorId: initialAnchorId,
  backToTimelineUrl,
  labels,
  milestoneLabels
}) => {
  const [activeAnchorId, setActiveAnchorId] = useState<string | undefined>(
    initialAnchorId || milestones?.[0]?.anchorId
  );

  const [hasStepper, setHasStepper] = useState(false);
  const navListRef = useRef<HTMLOListElement>(null);
  const navUserScrollUntil = useRef(0);
  // Holds the target during navigation so the scroll-spy doesn't highlight milestones we pass.
  const navTargetRef = useRef<string | null>(null);
  const navReleaseTimeout = useRef(0);
  const stopKeepingTargetInView = useRef<() => void>();

  const yearByAnchor = useMemo(() => {
    const map = new Map<string, string>();
    navigation?.forEach(group => group.items.forEach(item => map.set(item.anchorId, group.year)));
    return map;
  }, [navigation]);

  const activeYear = activeAnchorId && yearByAnchor.get(activeAnchorId);

  const isEditMode = useMemo(
    () => (milestones ?? []).some(milestone => milestone.onPageEditing || milestone.editUrl),
    [milestones]
  );

  // Scroll-spy: the first section (document order) crossing the reading band below the
  // scrubber is active, so a tall expanded card stays active until the next one reaches it.
  useEffect(() => {
    if (!milestones || milestones.length === 0) return;
    const sections = milestones
      .map(milestone => document.getElementById(milestone.anchorId))
      .filter((element): element is HTMLElement => element !== null);
    if (sections.length === 0) return;

    const intersecting = new Set<Element>();
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry =>
          entry.isIntersecting ? intersecting.add(entry.target) : intersecting.delete(entry.target)
        );
        const current = sections.find(section => intersecting.has(section));
        if (!current) return;

        // Mid-navigation: keep the target active until it reaches the band.
        if (navTargetRef.current) {
          if (current.id === navTargetRef.current) navTargetRef.current = null;
          return;
        }

        setActiveAnchorId(current.id);
      },
      { rootMargin: '-80px 0px -55% 0px' }
    );
    sections.forEach(section => observer.observe(section));

    // The last milestones may never reach the band. Force the final one active at the bottom.
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (navTargetRef.current) return;

        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
          setActiveAnchorId(sections[sections.length - 1].id);
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // User scrolled — they're taking over, so drop the pin and let the spy resume.
    const releaseNavTarget = () => {
      navTargetRef.current = null;
    };
    window.addEventListener('wheel', releaseNavTarget, { passive: true });
    window.addEventListener('touchstart', releaseNavTarget, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', releaseNavTarget);
      window.removeEventListener('touchstart', releaseNavTarget);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [milestones]);

  // Pin the active year to the top of the rail, unless the user just scrolled it themselves.
  useEffect(() => {
    const list = navListRef.current;
    if (!list || !activeYear || Date.now() < navUserScrollUntil.current) return;
    const group = list.querySelector<HTMLElement>(`[data-year="${activeYear}"]`);
    if (!group) return;
    const top = group.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    list.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [activeYear]);

  const onNavUserScroll = () => {
    navUserScrollUntil.current = Date.now() + navScrollGraceMs;
  };

  useEffect(
    () => () => {
      window.clearTimeout(navReleaseTimeout.current);
      stopKeepingTargetInView.current?.();
    },
    []
  );

  // Cards above the target can still change height (e.g. an image of unknown size loading), so re-align briefly.
  const keepTargetInView = useCallback((target: HTMLElement) => {
    stopKeepingTargetInView.current?.();
    const list = target.parentElement;
    if (!list || typeof ResizeObserver === 'undefined') return;

    const userEvents = ['wheel', 'touchstart', 'keydown'];
    const observer = new ResizeObserver(() => target.scrollIntoView({ behavior: 'auto', block: 'start' }));
    const stop = () => {
      observer.disconnect();
      window.clearTimeout(timeout);
      userEvents.forEach(type => window.removeEventListener(type, stop));
      stopKeepingTargetInView.current = undefined;
    };
    const timeout = window.setTimeout(stop, 2000);
    userEvents.forEach(type => window.addEventListener(type, stop, { passive: true }));
    observer.observe(list);
    stopKeepingTargetInView.current = stop;
  }, []);

  // Jump straight to the target — no smooth scroll when stepping through the timeline.
  const navigateTo = useCallback(
    (anchorId: string) => {
      const target = document.getElementById(anchorId);
      if (!target) return;
      setActiveAnchorId(anchorId);
      // Pin it so the scroll-spy doesn't fight us (see observer).
      navTargetRef.current = anchorId;
      window.clearTimeout(navReleaseTimeout.current);
      // Safety release if the target never reaches the band (e.g. a short final milestone).
      navReleaseTimeout.current = window.setTimeout(() => {
        navTargetRef.current = null;
      }, 1500);

      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      keepTargetInView(target);

      // Leave the URL alone inside the CMS preview frame.
      if (!isEditMode) history.replaceState(null, '', `#${anchorId}`);
    },
    [isEditMode, keepTargetInView]
  );

  // An event's own URL (or a hash) opens the timeline at that event.
  const initialTargetHandled = useRef(false);
  useEffect(() => {
    if (initialTargetHandled.current) return;
    initialTargetHandled.current = true;
    const target = initialAnchorId || window.location.hash.slice(1) || (isEditMode ? takeReturnTarget() : null);
    if (target && (milestones ?? []).some(milestone => milestone.anchorId === target)) {
      navigateTo(target);
    }
  }, [initialAnchorId, isEditMode, milestones, navigateTo]);

  const onAnchorClick = (anchorId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigateTo(anchorId);
  };

  return (
    <div className="kvinnehistorie-timeline-page">
      {header && <PageHeader {...header} />}
      {scrubber && (
        <KvinnehistorieScrubber
          {...scrubber}
          labels={labels}
          activeYear={activeYear || undefined}
          onNavigate={navigateTo}
          onScrub={navigateTo}
          onRailFitsChange={railFits => setHasStepper(!railFits)}
        />
      )}

      {backToTimelineUrl && (
        <ContentContainer className="kvinnehistorie-timeline-page--back">
          <a
            className="kvinnehistorie-timeline-page--back-link"
            href={backToTimelineUrl}
            target="_top"
            onClick={() => rememberReturnTarget(initialAnchorId)}
          >
            {labels?.backToTimeline || 'Tilbake til tidslinjen'}
          </a>
        </ContentContainer>
      )}

      <ContentContainer
        className={cn('kvinnehistorie-timeline-page--body', {
          'is-single': !navigation?.length && Boolean(milestones?.length),
          'has-stepper': hasStepper
        })}
      >
        {navigation && navigation.length > 0 && (
          <nav className="kvinnehistorie-timeline-page--nav" aria-label={labels?.navAriaLabel || 'Tidslinje'}>
            <ol
              className="kvinnehistorie-timeline-page--nav-list"
              ref={navListRef}
              onWheel={onNavUserScroll}
              onTouchMove={onNavUserScroll}
            >
              {navigation.map(group => (
                <li
                  className={cn('kvinnehistorie-timeline-page--nav-group', { 'is-active': group.year === activeYear })}
                  data-year={group.year}
                  key={group.year}
                >
                  <a
                    className="kvinnehistorie-timeline-page--nav-year"
                    href={`#${group.items[0]?.anchorId}`}
                    onClick={onAnchorClick(group.items[0]?.anchorId)}
                    aria-current={group.year === activeYear ? 'true' : undefined}
                  >
                    {group.year}
                  </a>
                  <ul className="kvinnehistorie-timeline-page--nav-items">
                    {group.items.map(item => (
                      <li key={item.anchorId}>
                        <a
                          className={cn('kvinnehistorie-timeline-page--nav-link', {
                            'is-active': item.anchorId === activeAnchorId
                          })}
                          href={`#${item.anchorId}`}
                          data-title={item.title}
                          onClick={onAnchorClick(item.anchorId)}
                          aria-current={item.anchorId === activeAnchorId ? 'true' : undefined}
                        >
                          <span className="kvinnehistorie-timeline-page--nav-link-text">{item.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {milestones?.length ? (
          <div className="kvinnehistorie-timeline-page--milestones">
            {milestones.map(milestone => (
              <KvinnehistorieMilestone
                key={milestone.anchorId}
                {...milestone}
                labels={milestoneLabels}
                initiallyOpen={milestone.anchorId === initialAnchorId}
              />
            ))}
          </div>
        ) : (
          <>
            <div className="kvinnehistorie-timeline-page--nav" aria-hidden="true">
              <ol className="kvinnehistorie-timeline-page--nav-list">
                {[0, 1, 2].map(index => (
                  <li className="kvinnehistorie-timeline-page--nav-group" key={index}>
                    <span className="kvinnehistorie-timeline-page--nav-placeholder" />
                  </li>
                ))}
              </ol>
            </div>
            <div className="kvinnehistorie-timeline-page--empty">
              <h2 className="kvinnehistorie-timeline-page--empty-heading">
                {labels?.emptyHeading || 'Ingen hendelser ennå'}
              </h2>
              <p className="kvinnehistorie-timeline-page--empty-text">
                {labels?.emptyText || 'Tidslinjen er ikke fylt ut ennå.'}
              </p>
            </div>
          </>
        )}
      </ContentContainer>
    </div>
  );
};

KvinnehistorieTimelinePage.displayName = 'KvinnehistorieTimelinePage';

export default KvinnehistorieTimelinePage;
